import React, { useState, useCallback, useEffect, memo } from "react";
import {
  Dialog,
  Slide,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Calendar } from "primereact/calendar";
import PlanningChannelSelector from "../PlaningChannelSelector";

// ─── Slide-up transition (stable ref so Dialog won't remount) ────
const SlideTransition = React.forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ═══════════════════════════════════════════════════════════════════
const PlanningFilterDialog = memo(
  ({
    open,
    onClose,
    onConfirm,
    ChannelList,
    lang,
    filterChannels,
    setFilterChannels,
    date,
    onDateChange,
    hideCloseIcon,
  }) => {
    const [localDate, setLocalDate] = useState(date);
    const [isConfirming, setIsConfirming] = useState(false);

    // Reset confirming state when dialog closes
    useEffect(() => {
      if (!open) setIsConfirming(false);
    }, [open]);

    const handleDateChange = useCallback(
      (e) => {
        setLocalDate(e.value);
        if (onDateChange) onDateChange(e);
      },
      [onDateChange]
    );

    const selectedCount = filterChannels?.length ?? 0;

    const showLoading = (show = false) => {
      setIsConfirming(show);
    }

    const handleConfirmClick = useCallback(() => {
      if (onConfirm) onConfirm(showLoading);

    }, [onConfirm]);

    return (
      <Dialog
        open={open}
        onClose={hideCloseIcon ? undefined : onClose}
        TransitionComponent={SlideTransition}
        maxWidth={false}
        disableEscapeKeyDown={!!hideCloseIcon}
        PaperProps={{
          sx: {
            width: 660,
            maxWidth: "95vw",
            borderRadius: "6px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            bgcolor: "#fff",
          },
        }}
      >
        {/* ─── Header (matches BossDialog: #3A3633) ────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.25,
            bgcolor: "#3A3633",
            color: "#fff",
            borderBottom: "1px solid #2e2b28",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontSize: 15,
              fontWeight: 500,
              fontFamily: '"Roboto", sans-serif',
              pl: 0.5,
            }}
          >
            {lang?.select_channels_dialog_header || "Select Channels"}
          </Typography>
          {selectedCount > 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.65)",
                mr: 1.5,
              }}
            >
              {selectedCount} channel{selectedCount !== 1 ? "s" : ""} selected
            </Typography>
          )}
          {!hideCloseIcon && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "#fff",
                p: 0.5,
                "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        {/* ─── Body ────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            px: 2.5,
            py: 2,
            gap: 2.5,
            fontFamily: '"Roboto", sans-serif',
            fontSize: 14,
            color: "#333",
          }}
        >
          {/* ── Left: Channels ──────────────── */}
          <Box sx={{ flex: "0 0 48%", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#3A3633",
                mb: 0.75,
              }}
            >
              {lang?.channel_label || "Channel"}:
            </Typography>
            <PlanningChannelSelector
              ChannelList={ChannelList?.sort((a, b) => a?.FullChannelName.localeCompare(b?.FullChannelName)) ?? []}
              lang={lang}
              setFilterChannels={setFilterChannels}
              filterChannels={filterChannels}
            />
          </Box>

          {/* ── Right: Calendar ─────────────── */}
          <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: "#3A3633",
                mb: 0.75,
              }}
            >
              {lang?.date_label || "Date"}:
            </Typography>
            <Box
              sx={{
                "& .p-datepicker": {
                  border: "none !important",
                  background: "transparent !important",
                  fontFamily: '"Roboto", sans-serif',
                  width: "100%",
                  fontSize: 13,
                },
                "& .p-datepicker table td > span.p-highlight": {
                  background: "#3A3633 !important",
                  color: "#fff !important",
                  borderRadius: "4px !important",
                },
                "& .p-datepicker table td > span:not(.p-highlight):not(.p-disabled):hover": {
                  background: "rgba(58,54,51,0.08) !important",
                  borderRadius: "4px !important",
                },
                "& .p-datepicker-header": {
                  borderBottom: "1px solid #e8e8e8 !important",
                  paddingBottom: "6px !important",
                },
              }}
            >
              <Calendar inline value={localDate} onChange={handleDateChange} />
            </Box>
          </Box>
        </Box>

        {/* ─── Footer note ─────────────────────────────────── */}
        <Typography
          sx={{
            px: 2.5,
            pb: 0.25,
            fontSize: 10,
            color: "#999",
            textAlign: "center",
            fontFamily: '"Roboto", sans-serif',
          }}
        >
          Note : Selecting multiple channels will remove the Additional Timezone.
        </Typography>

        {/* ─── Actions ─────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.5,
            py: 1.5,
            borderTop: "1px solid #eee",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            disabled={isConfirming}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              borderRadius: "4px",
              borderColor: "#ccc",
              color: "#555",
              px: 2.5,
              minWidth: 80,
              "&:hover": {
                borderColor: "#999",
                bgcolor: "#f5f5f5",
              },
            }}
          >
            {lang?.cancel_button_text || "Cancel"}
          </Button>
          <Button
            onClick={handleConfirmClick}
            variant="contained"
            size="small"
            disableElevation
            disabled={isConfirming}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              fontSize: 13,
              borderRadius: "4px",
              bgcolor: "#3A3633",
              color: "#fff",
              px: 2.5,
              minWidth: 80,
              "&:hover": {
                bgcolor: "#2a2623",
              },
              "&.Mui-disabled": {
                bgcolor: "#3A3633",
                color: "#fff",
                opacity: 0.85,
              },
            }}
          >
            {isConfirming ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              lang?.confirm_button_text || "Confirm"
            )}
          </Button>
        </Box>
      </Dialog>
    );
  }
);

PlanningFilterDialog.displayName = "PlanningFilterDialog";

export default PlanningFilterDialog;
